(() => {
  const FILTER_BLOCK = '[data-mafra-collection-filter]';
  const FILTER_ITEM = '[data-mafra-product-item]';

  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[–—]/g, '-')
      .trim();
  }

  function buildTokens(value) {
    return normalise(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function isMafraArabicLocale() {
    const lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    const dir = (document.documentElement.getAttribute('dir') || '').toLowerCase();
    const path = (window.location.pathname || '').toLowerCase();

    return lang.indexOf('ar') === 0 || dir === 'rtl' || path.indexOf('/ar') === 0;
  }

  function scrollTabsByAmount(tabsWrap, amount) {
    if (!tabsWrap) return;

    /* Arabic only: reverse the arrow click movement.
       English stays exactly the same. */
    const delta = isMafraArabicLocale() ? -amount : amount;

    try {
      tabsWrap.scrollBy({ left: delta, behavior: 'smooth' });
    } catch (error) {
      tabsWrap.scrollLeft += delta;
    }
  }

  function scrollTabIntoView(tab, tabsWrap) {
    if (!tab || !tabsWrap) return;

    const wrapRect = tabsWrap.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const tabCenter = tabRect.left + tabRect.width / 2;
    const wrapCenter = wrapRect.left + wrapRect.width / 2;
    const delta = tabCenter - wrapCenter;

    if (Math.abs(delta) < 12) return;

    try {
      tabsWrap.scrollBy({ left: delta, behavior: 'smooth' });
    } catch (error) {
      tabsWrap.scrollLeft += delta;
    }
  }

  function enableTabsDragScroll(tabsWrap) {
    if (!tabsWrap || tabsWrap.dataset.mafraDragReady === 'true') return;
    tabsWrap.dataset.mafraDragReady = 'true';

    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let hasDragged = false;

    tabsWrap.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      isDown = true;
      hasDragged = false;
      startX = event.clientX;
      startScrollLeft = tabsWrap.scrollLeft;
      tabsWrap.classList.add('is-dragging');
    });

    tabsWrap.addEventListener('pointermove', (event) => {
      if (!isDown) return;

      const walk = event.clientX - startX;
      if (Math.abs(walk) > 5) {
        hasDragged = true;
        tabsWrap.scrollLeft = startScrollLeft - walk;
      }
    });

    const stopDrag = () => {
      if (!isDown) return;
      isDown = false;
      tabsWrap.classList.remove('is-dragging');

      window.setTimeout(() => {
        hasDragged = false;
      }, 0);
    };

    tabsWrap.addEventListener('pointerup', stopDrag);
    tabsWrap.addEventListener('pointercancel', stopDrag);
    tabsWrap.addEventListener('pointerleave', stopDrag);

    tabsWrap.addEventListener(
      'click',
      (event) => {
        if (!hasDragged) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }



  function mafraSetArrowInactive(button, inactive) {
    if (!button) return;

    /* Preserve the arrow space so the filter panel never jumps/open-closes visually. */
    if (inactive) {
      button.disabled = true;
      button.setAttribute('aria-hidden', 'true');
      button.style.setProperty('visibility', 'hidden', 'important');
      button.style.setProperty('opacity', '0', 'important');
      button.style.setProperty('pointer-events', 'none', 'important');
    } else {
      button.disabled = false;
      button.setAttribute('aria-hidden', 'false');
      button.style.setProperty('visibility', 'visible', 'important');
      button.style.setProperty('opacity', '1', 'important');
      button.style.setProperty('pointer-events', 'auto', 'important');
    }
  }

  function mafraUpdateTabArrows(tabsWrap, prev, next, tabs) {
    if (!tabsWrap || !prev || !next || !tabs || tabs.length < 2) return;

    const canScroll = tabsWrap.scrollWidth > tabsWrap.clientWidth + 4;
    if (!canScroll) {
      mafraSetArrowInactive(prev, true);
      mafraSetArrowInactive(next, true);
      return;
    }

    const firstTab = tabs[0];
    const lastTab = tabs[tabs.length - 1];
    if (!firstTab || !lastTab) return;

    const wrapRect = tabsWrap.getBoundingClientRect();
    const firstRect = firstTab.getBoundingClientRect();
    const lastRect = lastTab.getBoundingClientRect();
    const prevRect = prev.getBoundingClientRect();
    const nextRect = next.getBoundingClientRect();
    const tolerance = 4;

    const firstVisible = firstRect.left >= wrapRect.left - tolerance && firstRect.right <= wrapRect.right + tolerance;
    const lastVisible = lastRect.left >= wrapRect.left - tolerance && lastRect.right <= wrapRect.right + tolerance;

    const firstCenter = firstRect.left + firstRect.width / 2;
    const prevCenter = prevRect.left + prevRect.width / 2;
    const nextCenter = nextRect.left + nextRect.width / 2;

    /*
      Physical side logic:
      - In English, first tab is on the left, so left arrow hides first.
      - In Arabic, first tab is on the right, so right arrow hides first.
      No RTL guessing, no layout reversing, no display:none.
    */
    const firstSideArrow = Math.abs(prevCenter - firstCenter) <= Math.abs(nextCenter - firstCenter) ? prev : next;
    const lastSideArrow = firstSideArrow === prev ? next : prev;

    mafraSetArrowInactive(firstSideArrow, firstVisible);
    mafraSetArrowInactive(lastSideArrow, lastVisible);
  }

  function initMafraCollectionFilters(root = document) {
    const blocks = root.querySelectorAll(FILTER_BLOCK);

    blocks.forEach((block) => {
      if (block.dataset.mafraReady === 'true') return;
      block.dataset.mafraReady = 'true';

      const toggle = block.querySelector('[data-mafra-filter-toggle]');
      const panel = block.querySelector('[data-mafra-filter-panel]');
      const tabsWrap = block.querySelector('[data-mafra-filter-tabs]');
      const tabs = Array.from(block.querySelectorAll('[data-mafra-filter-value]'));
      const sortSelect = block.querySelector('[data-mafra-sort]');
      const prev = block.querySelector('[data-mafra-filter-prev]');
      const next = block.querySelector('[data-mafra-filter-next]');
      const grid = document.querySelector('[data-mafra-grid]');
      const pagination = document.querySelector('[data-mafra-pagination]');
      const emptyMessage = document.querySelector('[data-mafra-empty-message]');

      let tabLayoutFrame = 0;
      const updateTabArrows = () => {
        if (tabLayoutFrame) return;

        tabLayoutFrame = window.requestAnimationFrame(() => {
          tabLayoutFrame = window.requestAnimationFrame(() => {
            tabLayoutFrame = 0;
            mafraUpdateTabArrows(tabsWrap, prev, next, tabs);
          });
        });
      };

      const centreTabAndUpdateArrows = (tab) => {
        if (tabLayoutFrame) {
          window.cancelAnimationFrame(tabLayoutFrame);
          tabLayoutFrame = 0;
        }

        tabLayoutFrame = window.requestAnimationFrame(() => {
          tabLayoutFrame = window.requestAnimationFrame(() => {
            tabLayoutFrame = 0;
            scrollTabIntoView(tab, tabsWrap);
            mafraUpdateTabArrows(tabsWrap, prev, next, tabs);
          });
        });
      };

      if (!grid) return;

      enableTabsDragScroll(tabsWrap);

      if (tabsWrap && tabsWrap.dataset.mafraArrowBoundaryReady !== 'true') {
        tabsWrap.dataset.mafraArrowBoundaryReady = 'true';
        tabsWrap.addEventListener('scroll', updateTabArrows, { passive: true });
        window.addEventListener('resize', updateTabArrows, { passive: true });
      }

      const items = Array.from(grid.querySelectorAll(FILTER_ITEM));
      const perPage = Math.max(1, Number(grid.dataset.mafraProductsPerPage || 16));

      let currentFilter = 'all';
      let currentSort = 'featured';
      let currentPage = 1;
      let matchingItems = items.slice();

      const setActiveTab = (targetTab) => {
        tabs.forEach((tab) => {
          const isActive = tab === targetTab;
          tab.classList.toggle('active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        centreTabAndUpdateArrows(targetTab);
      };

      const initialTab = tabs.find((tab) => normalise(tab.dataset.mafraFilterValue) === 'all') || tabs[0];
      if (initialTab) {
        setActiveTab(initialTab);
        currentFilter = initialTab.dataset.mafraFilterValue || 'all';
      }

      const sortItems = () => {
        const sorted = items.slice();

        sorted.sort((a, b) => {
          if (currentSort === 'price-ascending') {
            return Number(a.dataset.mafraSortPrice || 0) - Number(b.dataset.mafraSortPrice || 0);
          }

          if (currentSort === 'price-descending') {
            return Number(b.dataset.mafraSortPrice || 0) - Number(a.dataset.mafraSortPrice || 0);
          }

          if (currentSort === 'alpha-ascending') {
            return String(a.dataset.mafraSortTitle || '').localeCompare(String(b.dataset.mafraSortTitle || ''));
          }

          if (currentSort === 'alpha-descending') {
            return String(b.dataset.mafraSortTitle || '').localeCompare(String(a.dataset.mafraSortTitle || ''));
          }

          if (currentSort === 'created-descending') {
            return Number(b.dataset.mafraSortCreated || 0) - Number(a.dataset.mafraSortCreated || 0);
          }

          return Number(a.dataset.mafraOriginalIndex || 0) - Number(b.dataset.mafraOriginalIndex || 0);
        });

        const currentOrder = Array.from(grid.querySelectorAll(FILTER_ITEM));
        const orderChanged = sorted.some((item, index) => currentOrder[index] !== item);

        if (!orderChanged) return false;

        const fragment = document.createDocumentFragment();
        sorted.forEach((item) => fragment.appendChild(item));
        grid.appendChild(fragment);

        return true;
      };

      const getMatchingItems = () => {
        const value = normalise(currentFilter || 'all');
        const tokens = buildTokens(value);

        if (!tokens.length || value === 'all') {
          return items.slice();
        }

        return items.filter((item) => {
          const source = normalise(item.dataset.mafraFilterSource || '');
          return tokens.some((token) => source.includes(token));
        });
      };

      const renderPagination = () => {
        if (!pagination) return;

        const totalPages = Math.ceil(matchingItems.length / perPage);
        pagination.innerHTML = '';

        if (totalPages <= 1) return;

        const createButton = (label, page, className, disabled = false) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = className;
          button.textContent = label;
          button.disabled = disabled;
          button.dataset.mafraPage = String(page);

          if (page === currentPage && className.includes('__page')) {
            button.classList.add('active');
            button.setAttribute('aria-current', 'page');
          }

          return button;
        };

        const previousPageArrow = isMafraArabicLocale() ? '›' : '‹';
        const nextPageArrow = isMafraArabicLocale() ? '‹' : '›';

        pagination.appendChild(createButton(previousPageArrow, Math.max(1, currentPage - 1), 'mafra-collection-filter__page-arrow', currentPage === 1));

        const pages = [];
        for (let page = 1; page <= totalPages; page += 1) {
          if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1) {
            pages.push(page);
          }
        }

        let lastPage = 0;
        pages.forEach((page) => {
          if (page - lastPage > 1) {
            const ellipsis = document.createElement('span');
            ellipsis.className = 'mafra-collection-filter__page-ellipsis';
            ellipsis.textContent = '…';
            pagination.appendChild(ellipsis);
          }

          pagination.appendChild(createButton(String(page), page, 'mafra-collection-filter__page'));
          lastPage = page;
        });

        pagination.appendChild(createButton(nextPageArrow, Math.min(totalPages, currentPage + 1), 'mafra-collection-filter__page-arrow', currentPage === totalPages));
      };

      const applyState = () => {
        sortItems();
        matchingItems = getMatchingItems();

        const totalPages = Math.max(1, Math.ceil(matchingItems.length / perPage));
        currentPage = Math.min(currentPage, totalPages);

        const start = (currentPage - 1) * perPage;
        const end = start + perPage;
        const visibleSet = new Set(matchingItems.slice(start, end));
        const matchingSet = new Set(matchingItems);

        items.forEach((item) => {
          item.classList.toggle('mafra-hidden-by-filter', !matchingSet.has(item));
          item.classList.toggle('mafra-hidden-by-page', matchingSet.has(item) && !visibleSet.has(item));
        });

        if (emptyMessage) {
          if (matchingItems.length === 0) {
            emptyMessage.removeAttribute('hidden');
          } else {
            emptyMessage.setAttribute('hidden', '');
          }
        }

        renderPagination();
      };

      toggle?.addEventListener('click', () => {
        const isOpen = panel && !panel.hasAttribute('hidden');

        if (isOpen) {
          panel.setAttribute('hidden', '');
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          panel.removeAttribute('hidden');
          toggle.setAttribute('aria-expanded', 'true');
          centreTabAndUpdateArrows(
            block.querySelector('[data-mafra-filter-value].active')
          );
        }
      });

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          currentFilter = tab.dataset.mafraFilterValue || 'all';
          currentPage = 1;
          setActiveTab(tab);
          applyState();
          updateTabArrows();
        });
      });

      sortSelect?.addEventListener('change', () => {
        currentSort = sortSelect.value || 'featured';
        currentPage = 1;
        applyState();
      });

      const forceAbsolutePageTop = () => {
        const html = document.documentElement;
        const body = document.body;

        const previousHtmlScrollBehavior = html ? html.style.scrollBehavior : '';
        const previousBodyScrollBehavior = body ? body.style.scrollBehavior : '';

        if (html) html.style.scrollBehavior = 'auto';
        if (body) body.style.scrollBehavior = 'auto';

        const goTop = () => {
          window.scrollTo(0, 0);
          if (html) html.scrollTop = 0;
          if (body) body.scrollTop = 0;
        };

        goTop();
        requestAnimationFrame(goTop);
        window.setTimeout(goTop, 30);
        window.setTimeout(goTop, 90);
        window.setTimeout(goTop, 180);
        window.setTimeout(goTop, 350);
        window.setTimeout(goTop, 700);

        window.setTimeout(() => {
          if (html) html.style.scrollBehavior = previousHtmlScrollBehavior;
          if (body) body.style.scrollBehavior = previousBodyScrollBehavior;
        }, 800);
      };

      pagination?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-mafra-page]');
        if (!button || button.disabled) return;

        currentPage = Number(button.dataset.mafraPage || 1);
        applyState();
        forceAbsolutePageTop();
      });

      prev?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const amount = Math.max(180, Math.round((tabsWrap?.clientWidth || 240) * 0.82));
        scrollTabsByAmount(tabsWrap, -amount);
        updateTabArrows();
      });

      next?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const amount = Math.max(180, Math.round((tabsWrap?.clientWidth || 240) * 0.82));
        scrollTabsByAmount(tabsWrap, amount);
        updateTabArrows();
      });

      applyState();
      updateTabArrows();
    });
  }

  document.addEventListener('DOMContentLoaded', () => initMafraCollectionFilters());

  document.addEventListener('shopify:section:load', (event) => {
    initMafraCollectionFilters(event.target);
  });
})();
/* =========================================================
   SAFE LABEL FIX — SINGLE SOURCE OF TRUTH
   Labels come only from the collection section data attributes.
   This prevents a second hard-coded translation from being
   written over or beneath the server-rendered label.
========================================================= */
(function () {
  function isArabicFilterBlock(block) {
    var html = document.documentElement;
    var lang = ((html && html.getAttribute('lang')) || '').toLowerCase();
    var pageDir = ((html && html.getAttribute('dir')) || '').toLowerCase();
    var blockDir = ((block && block.getAttribute('dir')) || '').toLowerCase();
    var path = (window.location.pathname || '').toLowerCase();

    return (
      lang.indexOf('ar') === 0 ||
      pageDir === 'rtl' ||
      blockDir === 'rtl' ||
      path === '/ar' ||
      path.indexOf('/ar/') === 0
    );
  }

  function getFilterLabel(block, isOpen) {
    var suffix = isArabicFilterBlock(block) ? 'Ar' : 'En';
    var key = isOpen
      ? 'mafraFilterOpenLabel' + suffix
      : 'mafraFilterLabel' + suffix;

    var value = block.dataset[key];

    if (value) return value;

    if (suffix === 'Ar') {
      return isOpen ? 'تصفية' : 'تصفية وترتيب';
    }

    return isOpen ? 'Filter' : 'Filter and sort';
  }

  function setToggleText(toggle, text) {
    if (!toggle || !text) return;

    /*
      Update only the dedicated label.
      Never use toggle.querySelector('span'), because that may target
      an icon/wrapper and leave another translated label underneath.
    */
    var label = toggle.querySelector('[data-mafra-ui-label="filter"]');

    if (!label) return;

    if (label.textContent !== text) {
      label.textContent = text;
    }

    if (toggle.getAttribute('aria-label') !== text) {
      toggle.setAttribute('aria-label', text);
    }
  }

  function syncMafraFilterLabel(root) {
    (root || document)
      .querySelectorAll('[data-mafra-collection-filter]')
      .forEach(function (block) {
        var toggle = block.querySelector('[data-mafra-filter-toggle]');
        var panel = block.querySelector('[data-mafra-filter-panel]');

        if (!toggle || !panel) return;

        var isOpen =
          toggle.getAttribute('aria-expanded') === 'true' ||
          !panel.hasAttribute('hidden');

        setToggleText(toggle, getFilterLabel(block, isOpen));
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    syncMafraFilterLabel(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    syncMafraFilterLabel(event.target);
  });

  document.addEventListener(
    'click',
    function (event) {
      if (!event.target.closest('[data-mafra-filter-toggle]')) return;

      window.setTimeout(function () {
        syncMafraFilterLabel(document);
      }, 0);

      window.setTimeout(function () {
        syncMafraFilterLabel(document);
      }, 80);
    },
    true
  );

  new MutationObserver(function (mutations) {
    var shouldSync = mutations.some(function (mutation) {
      return (
        mutation.type === 'attributes' &&
        (
          mutation.attributeName === 'hidden' ||
          mutation.attributeName === 'aria-expanded' ||
          mutation.attributeName === 'lang' ||
          mutation.attributeName === 'dir'
        )
      );
    });

    if (shouldSync) {
      syncMafraFilterLabel(document);
    }
  }).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['hidden', 'aria-expanded', 'lang', 'dir']
  });
})();

/* =========================================================
   Fallback only for any normal Shopify pagination links.
   Main MA-FRA pagination uses buttons and is handled above.
========================================================= */
(function () {
  var KEY = 'mafraNormalPaginationTop';

  function goTop() {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href*="page="]');
    if (!link) return;
    sessionStorage.setItem(KEY, '1');
  }, true);

  function run() {
    if (sessionStorage.getItem(KEY) !== '1') return;
    sessionStorage.removeItem(KEY);
    goTop();
    requestAnimationFrame(goTop);
    setTimeout(goTop, 80);
    setTimeout(goTop, 250);
    setTimeout(goTop, 600);
  }

  document.addEventListener('DOMContentLoaded', run);
  window.addEventListener('pageshow', run);
})();

/* =========================================================
   STRONG FIX: hide MA-FRA filter arrows when cart/menu drawer opens
   Fixes arrows showing above cart drawer/menu in Arabic
   Does not change filter layout, tabs, sort, or arrow click logic
========================================================= */
(function () {
  var ARROW_SELECTOR = [
    '[data-mafra-collection-filter] [data-mafra-filter-prev]',
    '[data-mafra-collection-filter] [data-mafra-filter-next]',
    '[data-mafra-collection-filter] .mafra-collection-filter__arrow'
  ].join(',');

  function drawerLooksOpen() {
    var body = document.body;
    var html = document.documentElement;

    if (!body || !html) return false;

    if (
      body.classList.contains('overflow-hidden') ||
      body.classList.contains('overflow-hidden-mobile') ||
      body.classList.contains('menu-opening') ||
      html.classList.contains('overflow-hidden') ||
      html.classList.contains('overflow-hidden-mobile')
    ) {
      return true;
    }

    var drawerSelectors = [
      'cart-drawer.active',
      'cart-drawer[open]',
      '.cart-drawer.active',
      '.cart-drawer[open]',
      '#CartDrawer.active',
      '#CartDrawer[open]',
      '.drawer.active',
      '.drawer[open]',
      'menu-drawer[open]',
      'menu-drawer details[open]',
      'header-drawer details[open]',
      '#menu-drawer[open]',
      '.menu-drawer[open]',
      '.menu-drawer.active',
      'details[open] > .menu-drawer',
      'details[open] .menu-drawer'
    ];

    return drawerSelectors.some(function (selector) {
      return document.querySelector(selector);
    });
  }

  function applyArrowDrawerFix() {
    var shouldHide = drawerLooksOpen();

    document.querySelectorAll(ARROW_SELECTOR).forEach(function (arrow) {
      if (shouldHide) {
        arrow.dataset.mafraDrawerHidden = 'true';
        arrow.style.setProperty('visibility', 'hidden', 'important');
        arrow.style.setProperty('opacity', '0', 'important');
        arrow.style.setProperty('pointer-events', 'none', 'important');
        arrow.style.setProperty('z-index', '-1', 'important');
      } else if (arrow.dataset.mafraDrawerHidden === 'true') {
        arrow.removeAttribute('data-mafra-drawer-hidden');
        arrow.style.removeProperty('visibility');
        arrow.style.removeProperty('opacity');
        arrow.style.removeProperty('pointer-events');
        arrow.style.removeProperty('z-index');
      }
    });
  }

  document.addEventListener('click', function () {
    setTimeout(applyArrowDrawerFix, 20);
    setTimeout(applyArrowDrawerFix, 120);
    setTimeout(applyArrowDrawerFix, 350);
    setTimeout(applyArrowDrawerFix, 700);
  }, true);

  document.addEventListener('DOMContentLoaded', applyArrowDrawerFix);
  document.addEventListener('shopify:section:load', applyArrowDrawerFix);

  new MutationObserver(applyArrowDrawerFix).observe(document.documentElement, {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: ['class', 'open', 'aria-hidden', 'hidden']
  });

  applyArrowDrawerFix();
})();